import { useEffect, useState } from "react";
import { Paperclip, X, FileText, Download, ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

/** Uploads receipts to /uploads/receipt and keeps an array of file records in value. */
export function ReceiptUploader({ value = [], onChange, testId = "receipt-uploader" }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const added = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { const { data } = await api.post("/uploads/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } }); added.push(data); }
      catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    onChange([...value, ...added]);
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-input bg-secondary/40 text-sm text-muted-foreground cursor-pointer hover:border-primary/60 transition-colors">
        <Paperclip size={14} /> {busy ? "Uploading…" : "Attach receipt (image or PDF, max 10MB)"}
        <input data-testid="receipt-file-input" type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={pick} disabled={busy} />
      </label>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((f) => (
            <span key={f.id} data-testid={`receipt-chip-${f.id}`} className="inline-flex items-center gap-1.5 text-xs bg-card border border-border rounded-full pl-2.5 pr-1 py-1">
              <FileText size={12} className="text-primary" /> <span className="max-w-[140px] truncate">{f.original_filename}</span>
              <button type="button" data-testid={`receipt-remove-${f.id}`} onClick={() => onChange(value.filter((x) => x.id !== f.id))} className="rounded-full p-0.5 hover:bg-secondary"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Fetches a protected file as blob and shows a thumbnail / opener. */
export function AttachmentList({ files = [] }) {
  if (!files.length) return null;
  return (
    <div className="space-y-1.5" data-testid="attachment-list">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Receipts · {files.length}</div>
      <div className="flex flex-wrap gap-2">{files.map((f) => <Attachment key={f.id} f={f} />)}</div>
    </div>
  );
}

function Attachment({ f }) {
  const [url, setUrl] = useState(null);
  const isImg = (f.content_type || "").startsWith("image/");
  useEffect(() => {
    let u;
    api.get(`/files/${f.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [f.id]);
  return (
    <a href={url || "#"} target="_blank" rel="noreferrer" data-testid={`attachment-${f.id}`} className="group flex items-center gap-2 rounded-lg border border-border bg-card p-1.5 pr-3 hover:border-primary/60 transition-colors">
      {isImg && url ? <img src={url} alt={f.original_filename} className="w-12 h-12 object-cover rounded-md" /> : <div className="w-12 h-12 rounded-md bg-secondary grid place-items-center"><FileText size={18} className="text-primary" /></div>}
      <div className="text-xs"><div className="font-medium max-w-[140px] truncate">{f.original_filename}</div><div className="text-muted-foreground flex items-center gap-1">{Math.round((f.size || 0) / 1024)} KB <ExternalLink size={10} /></div></div>
    </a>
  );
}

export async function downloadBlob(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  const u = URL.createObjectURL(r.data);
  const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 2000);
}

export function DownloadButton({ path, filename, label = "Download PDF", testId }) {
  const [busy, setBusy] = useState(false);
  return (
    <button data-testid={testId} disabled={busy} onClick={async () => { setBusy(true); try { await downloadBlob(path, filename); } catch (e) { toast.error(errMsg(e)); } setBusy(false); }}
      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-border bg-card text-sm hover:border-primary/60 transition-colors disabled:opacity-60">
      <Download size={14} /> {busy ? "Preparing…" : label}
    </button>
  );
}
